// apps/admin/src/lib/shell.js
//
// Off-canvas drawer wiring for the sidebar/header app shell (spec §3.1/§3.7
// tier 3, §7, §3.8). Replaces the inline nav-toggle <script> that used to live
// in index.html (queried `.admin-topbar`/`.admin-hamburger`) now that the
// shell markup ships in the bundle instead of an inline page script.
//
// Task 6.1 adds the accessibility contract spec §3.8 requires while the
// drawer is open (<760px tier only — the sidebar is always-visible, non-modal
// content at wider viewports):
//   - focus is TRAPPED inside `.sidebar` (Tab/Shift-Tab wrap at its edges,
//     and stray focus is pulled back in — self-contained here, not the
//     shared `src/client/modules/focus-trap.js` modal manager, which is a
//     different app's dependency this module deliberately doesn't take on);
//   - the background (`.topbar`'s children other than the hamburger itself,
//     plus the main content) is made BOTH `inert` and `aria-hidden="true"` —
//     removed from keyboard reach and the accessibility tree — while leaving
//     the hamburger and the scrim as the drawer's own close affordances;
//   - Escape closes the drawer;
//   - closing ALWAYS restores focus to the hamburger and ALWAYS releases the
//     background `inert`/`aria-hidden`, regardless of which of the three
//     close paths (Escape, scrim click, hamburger re-click) fired;
//   - a resize that crosses OUT of the <760px tier force-closes any open
//     drawer state so the shell never stays "background inert" once the
//     sidebar reverts to its normal, always-visible desktop/tablet layout.

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function isVisible(el) {
  return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}

function focusableIn(container) {
  return [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter(isVisible);
}

// The drawer's TRAPPABLE focusables: every visible focusable in the sidebar EXCEPT
// the brand link. At the <760px drawer tier the sidebar sits at top:0 behind the
// raised topbar (z-index 80 over the sidebar's 70), so the brand — the sidebar's
// first focusable — is obscured. Both the initial focus AND the Tab-trap wrap use
// this set so keyboard focus never lands on the hidden brand (WCAG 2.1 SC 2.4.7).
function trapFocusablesIn(container) {
  return focusableIn(container).filter((el) => !el.classList.contains('admin-brand'));
}

export function initShell(root = document) {
  const app = root.querySelector('.app');
  const hamburger = root.querySelector('.hamburger');
  if (!app || !hamburger) return;

  const scrim = root.querySelector('.scrim');
  const sidebar = root.querySelector('.sidebar');
  const content = root.querySelector('.content');
  // The <760px drawer tier query, captured ONCE. Guarded for happy-dom/older
  // engines where matchMedia is undefined or lacks addEventListener.
  const win = root.defaultView || (typeof window !== 'undefined' ? window : null);
  const mobileQuery = typeof win?.matchMedia === 'function' ? win.matchMedia('(max-width: 759px)') : null;
  const isMobileTier = () => !!mobileQuery?.matches;
  // Everything the drawer sits in front of, EXCEPT the hamburger (which must
  // stay operable — both to re-close by click and as the guaranteed close
  // focus target) and the scrim (the drawer's own click-outside affordance,
  // not "background" it covers).
  const inertTargets = [
    ...root.querySelectorAll('.topbar > :not(.hamburger)'),
    content,
  ].filter(Boolean);

  function setBackgroundInert(on) {
    for (const el of inertTargets) {
      if (on) {
        el.inert = true;
        el.setAttribute('aria-hidden', 'true');
      } else {
        el.inert = false;
        el.removeAttribute('aria-hidden');
      }
    }
  }

  // The CLOSED drawer at the <760px tier is only translated off-screen
  // (transform: translateX(-100%)) — visually gone, but its links would remain in
  // the keyboard order AND the accessibility tree, sitting BEFORE the hamburger. So
  // give the closed drawer a real semantic state: mark `.sidebar` inert +
  // aria-hidden whenever it is a closed off-canvas drawer, and clear that the moment
  // it opens OR the layout leaves the drawer tier (where the sidebar is normal,
  // always-visible grid content again). Only ever active at the mobile tier; a
  // resize across the boundary re-syncs it.
  function syncSidebarClosed() {
    if (!sidebar) return;
    const hidden = isMobileTier() && !app.classList.contains('nav-open');
    if (hidden) {
      // Rescue focus BEFORE inerting: if focus is currently inside the sidebar (e.g. a
      // resize just crossed INTO the drawer tier while a desktop sidebar link was
      // focused), move it to the hamburger first. Applying `inert` does not reliably
      // relocate an already-focused descendant, so it would otherwise strand keyboard
      // focus on the now-off-canvas element (WCAG 2.1 SC 2.4.7). The three real close
      // paths already move focus themselves; this makes the passive tier crossing safe too.
      const activeEl = (root.ownerDocument || root).activeElement;
      if (activeEl && sidebar.contains(activeEl)) hamburger.focus();
      sidebar.inert = true;
      sidebar.setAttribute('aria-hidden', 'true');
    } else {
      sidebar.inert = false;
      sidebar.removeAttribute('aria-hidden');
    }
  }

  // Apply/clear the open state WITHOUT any focus side effect. The dismiss paths
  // (Escape/scrim/hamburger) route focus back to the hamburger; the navigation
  // path routes it to the newly-rendered content instead — so the focus move is
  // kept out here and each caller owns its own destination.
  function applyOpenState(open) {
    app.classList.toggle('nav-open', open);
    hamburger.setAttribute('aria-expanded', open ? 'true' : 'false');
    setBackgroundInert(open);
    // Re-sync the drawer's own inert state AFTER toggling nav-open: opening clears it
    // (so the trap can focus a nav item next), closing restores it at the mobile tier.
    syncSidebarClosed();
  }

  function setOpen(open) {
    if (open === app.classList.contains('nav-open')) return;
    applyOpenState(open);
    if (open) {
      // Move focus onto the first TRAPPABLE focusable (trapFocusablesIn) — the first
      // nav item, never the brand link, which is obscured behind the raised topbar
      // at the drawer tier and would give an invisible focus ring (WCAG 2.1 SC 2.4.7).
      trapFocusablesIn(sidebar || app)[0]?.focus();
    } else {
      // ALWAYS the hamburger, regardless of what had focus before opening —
      // Escape, scrim-click, and hamburger re-click all converge here (spec
      // §3.8: "restores focus to the hamburger").
      hamburger.focus();
    }
  }

  // The fourth close path: a same-app navigation COMMITTED from a nav link
  // inside the open drawer. Unlike the three dismiss paths, the user is
  // navigating (not merely closing), so focus moves to the newly-rendered
  // content landmark (`.content` / `#admin-root`) — NOT back to the hamburger,
  // and NOT trapped in the (now closing) sidebar. Releasing the background
  // `inert` BEFORE focusing is required: an inert element cannot take focus.
  function closeForNavigation() {
    if (!app.classList.contains('nav-open')) return;
    applyOpenState(false);
    if (content) {
      // `.content` (<main id="admin-root">) is not focusable by default; make it
      // programmatically focusable so focus lands on the just-revealed screen's
      // container instead of being stranded on a now-hidden sidebar control.
      if (!content.hasAttribute('tabindex')) content.setAttribute('tabindex', '-1');
      content.focus();
    }
  }

  hamburger.addEventListener('click', () => {
    setOpen(!app.classList.contains('nav-open'));
  });

  if (scrim) {
    scrim.addEventListener('click', () => setOpen(false));
  }

  // Fourth close path — a nav link clicked INSIDE the open drawer. The router's
  // global interceptor (router.js attachLinkInterceptor) performs the actual
  // navigation; this listener only manages the drawer + focus and never calls
  // preventDefault, so navigation still proceeds. Bound on `.sidebar` (closer to
  // the clicked link than the router's document-level handler), it runs first in
  // the bubble phase and hands focus to `.content` before the router reveals the
  // new screen — the persistent `#admin-root` node keeps that focus across the
  // synchronous section swap. The guards mirror the router's own so the drawer
  // only closes when the router will actually navigate IN-PLACE (a modified /
  // middle / new-tab / download click is a real navigation-away, left alone).
  if (sidebar) {
    sidebar.addEventListener('click', (e) => {
      if (!app.classList.contains('nav-open')) return;
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest('a[href]');
      if (!a || !sidebar.contains(a)) return;
      const href = a.getAttribute('href');
      if (!href || !href.startsWith('/admin')) return;
      if (a.target && a.target !== '_self') return;
      if (a.hasAttribute('download')) return;
      closeForNavigation();
    });
  }

  root.addEventListener('keydown', (e) => {
    if (!app.classList.contains('nav-open')) return;
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key !== 'Tab' || !sidebar) return;
    // Self-contained Tab trap: confine the cycle to the sidebar's OWN TRAPPABLE
    // focusables (trapFocusablesIn EXCLUDES the obscured brand link) regardless of
    // what else is technically still focusable on the page (the hamburger,
    // deliberately left non-inert above) — this handler intercepts every Tab press
    // first and wins over native order. Excluding the brand keeps BOTH wrap
    // directions on unobscured nav items (WCAG 2.1 SC 2.4.7): Tab from the last
    // item wraps to the first NAV item, not to the brand hidden under the topbar.
    const focusables = trapFocusablesIn(sidebar);
    if (!focusables.length) { e.preventDefault(); return; }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const activeEl = (root.ownerDocument || root).activeElement;
    if (!sidebar.contains(activeEl)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }
    if (!e.shiftKey && activeEl === last) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && activeEl === first) {
      e.preventDefault();
      last.focus();
    }
  });

  // Crossing tiers must not strand the shell in a stale state. Leaving the <760px
  // drawer tier reverts the sidebar to its normal, always-visible grid placement, so
  // force-close any open drawer (clear `.nav-open`, `aria-expanded`, and the
  // background inert) WITHOUT stealing focus onto the hamburger the way a real close
  // does. Either direction then re-syncs the drawer's OWN inert state: entering the
  // tier with the drawer closed makes the sidebar inert; leaving it clears that.
  // Guarded for happy-dom/older engines where matchMedia lacks addEventListener.
  if (mobileQuery) {
    const onTierChange = (e) => {
      if (!e.matches && app.classList.contains('nav-open')) {
        app.classList.remove('nav-open');
        hamburger.setAttribute('aria-expanded', 'false');
        setBackgroundInert(false);
      }
      syncSidebarClosed();
    };
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', onTierChange);
    else mobileQuery.addListener?.(onTierChange); // Safari <14 fallback
  }

  // Establish the drawer's initial closed-state semantics — inert when the page loads
  // at the mobile tier with the drawer closed (the default), cleared otherwise.
  syncSidebarClosed();
}
